import { useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getPartnerBySlug } from "@/data/partners";
import { Phone, Globe, Shield, Clock, MapPin } from "lucide-react";

const TJP_LOGO = "https://customer-assets.emergentagent.com/job_b99f5c04-b5b6-42f3-ace7-113e95402a7c/artifacts/d0rzk6rg_17997253177043219.jpg";
const WHATSAPP_NUMBER = "447780996775";

const PartnerPage = () => {
  const { slug } = useParams();
  const partner = getPartnerBySlug(slug);

  // Redirect to home if partner not found
  if (!partner) {
    return <Navigate to="/" replace />;
  }

  const {
    partner_name,
    partner_logo,
    hook,
    intro,
    case_1_img,
    case_1_caption,
    case_2_img,
    case_2_caption,
    case_3_img,
    case_3_caption,
    how_bullets,
    cta_body
  } = partner;

  const whatsappMessage = encodeURIComponent(
    `Hi Tony, I'm reaching out from ${partner_name}. I'd like to book a 20-minute intro call to discuss potential collaboration.`
  );
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="partner-page">
      <Helmet>
        <title>{partner_name + " x TJP Construction"}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="max-w-[800px] mx-auto px-6 py-12 md:py-16" data-testid="partner-content">
        
        {/* Hero Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between gap-6 mb-8">
            <img 
              src={TJP_LOGO} 
              alt="TJP Construction" 
              className="h-16 w-auto object-contain"
            />
            {partner_logo ? (
              <img 
                src={partner_logo} 
                alt={partner_name} 
                className="h-16 w-auto object-contain"
              />
            ) : (
              <div className="h-16 px-4 bg-slate-100 rounded-lg flex items-center justify-center">
                <span className="text-slate-600 font-medium text-sm">{partner_name}</span>
              </div>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-3" data-testid="partner-hero-title">
            {partner_name}, meet TJP
          </h1>
          <p className="text-xl text-amber-700 font-medium">
            {hook}
          </p>
        </section>

        {/* Intro */}
        <section className="mb-12">
          <p className="text-lg text-stone-600 leading-relaxed">
            {intro}
          </p>
        </section>

        {/* Projects Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            Projects that would suit your work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: "case-1", img: case_1_img, caption: case_1_caption },
              { id: "case-2", img: case_2_img, caption: case_2_caption },
              { id: "case-3", img: case_3_img, caption: case_3_caption }
            ].map((project) => (
              <div key={project.id} className="bg-white rounded-lg overflow-hidden shadow-md" data-testid={`partner-project-card-${project.id}`}>
                <div className="h-48 bg-stone-200">
                  <img 
                    src={project.img} 
                    alt={project.caption}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="p-4 text-sm text-stone-600">
                  {project.caption}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* How We Work Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            How we'd work together
          </h2>
          <ul className="space-y-4">
            {how_bullets.map((bullet, index) => (
              <li key={`how-${index}-${bullet.slice(0, 20).replace(/\s+/g, '-')}`} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-700 font-bold text-sm">{index + 1}</span>
                </div>
                <span className="text-stone-600">{bullet}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Credibility Strip */}
        <section className="mb-12 py-6 border-y border-stone-200">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-stone-500">
            <span className="flex items-center gap-2">
              <Clock size={16} className="text-amber-600" />
              15+ years trading
            </span>
            <span className="flex items-center gap-2">
              <Shield size={16} className="text-amber-600" />
              Fully insured
            </span>
            <span className="flex items-center gap-2">
              <MapPin size={16} className="text-amber-600" />
              Surrey & SW London
            </span>
            <span className="flex items-center gap-2">
              <Globe size={16} className="text-amber-600" />
              tjpconstruction.co.uk
            </span>
          </div>
        </section>

        {/* CTA Block */}
        <section className="bg-slate-900 rounded-xl p-8 text-center mb-12">
          <h2 className="text-2xl font-bold text-white mb-3">
            Book a 20-minute intro call with Tony
          </h2>
          <p className="text-stone-300 mb-6">
            {cta_body}
          </p>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="partner-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-500 transition-colors"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Message on WhatsApp
          </a>
        </section>

      </main>

      {/* Minimal Footer */}
      <footer className="border-t border-stone-200 py-8">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src={TJP_LOGO} 
              alt="TJP Construction" 
              className="h-10 w-auto"
            />
            <div className="flex items-center gap-6 text-sm text-stone-500">
              <a href="tel:07780996775" className="flex items-center gap-2 hover:text-slate-900">
                <Phone size={16} />
                07780 996775
              </a>
              <a href="https://tjpconstruction.co.uk" className="flex items-center gap-2 hover:text-slate-900">
                <Globe size={16} />
                tjpconstruction.co.uk
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PartnerPage;
